import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowUpRight,
    ChevronDown,
    PhoneCallIcon,
    ThumbsDownIcon,
    ThumbsUpIcon,
} from "lucide-react";
import type { SentimentTheme } from "@type/aiqm";
import { getSentimentBgColor } from "@util/agent-scoring";
import { cn } from "@util/tailwind";
import { Pagination } from "@component/pagination";

// Themes per page in each column. Paginating (rather than "show all") keeps both
// columns a bounded, consistent height with the footer pinned to the bottom.
const PAGE_SIZE = 5;

function netLabel(score: number): string {
    return `${score > 0 ? "+" : ""}${score}`;
}

function BreakdownBar({ theme }: { theme: SentimentTheme }) {
    const { positive, neutral, negative } = theme.breakdown;
    return (
        <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="bg-emerald-400" style={{ width: `${positive}%` }} title={`Positive ${positive}%`} />
            <div className="bg-gray-300" style={{ width: `${neutral}%` }} title={`Neutral ${neutral}%`} />
            <div className="bg-rose-400" style={{ width: `${negative}%` }} title={`Negative ${negative}%`} />
        </div>
    );
}

function ThemeRow({ theme }: { theme: SentimentTheme }) {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    // Level + call-share are pre-computed by the BE — rendered directly.
    const level = theme.sentiment_level;
    const hasDrilldown = theme.example_call_ids.length > 0;
    const sharePct = theme.call_pct;
    const exampleCallLabel = (index: number) =>
        `Open example call ${index + 1} for ${theme.label}`;

    return (
        <div className="rounded-lg border border-gray-100 bg-white transition-colors hover:border-gray-200">
            <button
                type="button"
                onClick={() => hasDrilldown && setExpanded((e) => !e)}
                className={cn(
                    "flex w-full items-center gap-2.5 px-3 py-2.5 text-left",
                    hasDrilldown ? "cursor-pointer" : "cursor-default"
                )}
                aria-expanded={hasDrilldown ? expanded : undefined}
            >
                <div className="min-w-0 flex-1">
                    <p
                        className="truncate text-sm font-medium text-gray-900"
                        title={theme.label}
                    >
                        {theme.label}
                    </p>
                    <div className="mt-1.5 flex items-center gap-2 pl-0">
                        <div className="max-w-[160px] flex-1">
                            <BreakdownBar theme={theme} />
                        </div>
                        <span className="whitespace-nowrap text-xs text-gray-400">
                            {theme.call_count} call{theme.call_count === 1 ? "" : "s"}
                            {sharePct > 0 && (
                                <span className="ml-1 text-gray-300">· {sharePct}%</span>
                            )}
                        </span>
                    </div>
                </div>
                <span
                    className={cn(
                        "flex-shrink-0 rounded-md px-2 py-1 text-xs font-semibold tabular-nums",
                        getSentimentBgColor(level)
                    )}
                    title={`Average net sentiment ${netLabel(theme.avg_sentiment)}`}
                >
                    {netLabel(theme.avg_sentiment)}
                </span>
                {/* Expand affordance — right-aligned, vertically centered. Rotates
                    to point up when the example calls are open. */}
                {hasDrilldown && (
                    <ChevronDown
                        className={cn(
                            "h-4 w-4 flex-shrink-0 text-gray-400 transition-transform",
                            expanded && "rotate-180"
                        )}
                    />
                )}
            </button>

            {expanded && hasDrilldown && (
                <div className="border-t border-gray-100 bg-gray-50 px-3 py-2.5">
                    <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Example calls
                        <span className="ml-1 text-gray-300">
                            ({theme.example_call_ids.length})
                        </span>
                    </p>
                    {/* Compact numbered pills instead of a long list of repetitive
                        text links — far less overwhelming when a theme has many
                        examples, while each still opens the call detail. */}
                    <div className="flex flex-wrap gap-1.5">
                        {theme.example_call_ids.map((id, index) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => navigate(`/training/aiqm/${id}`)}
                                aria-label={exampleCallLabel(index)}
                                title={exampleCallLabel(index)}
                                className="group inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                            >
                                <PhoneCallIcon className="h-3 w-3 text-gray-400 group-hover:text-blue-500" />
                                Call {index + 1}
                                <ArrowUpRight className="h-3 w-3 text-gray-300 group-hover:text-blue-500" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ThemeColumn({
    title,
    icon,
    accent,
    themes,
    emptyText,
}: {
    title: string;
    icon: React.ReactNode;
    accent: string;
    themes: SentimentTheme[];
    emptyText: string;
}) {
    // 0-based page state, converted to the 1-based <Pagination> API (matches the
    // dashboard Pager pattern).
    const [page, setPage] = useState(0);
    const totalPages = Math.max(1, Math.ceil(themes.length / PAGE_SIZE));
    // Clamp so a filter/granularity change that shrinks the list can't strand the
    // user on a now-out-of-range page.
    const safePage = Math.min(page, totalPages - 1);
    const visible = themes.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

    return (
        <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 px-4 py-3">
                <span className={accent}>{icon}</span>
                <h3 className="text-base font-semibold text-gray-800">{title}</h3>
                <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                    {themes.length}
                </span>
            </div>
            <div className="flex flex-1 flex-col p-3">
                {themes.length === 0 ? (
                    <p className="px-1 py-8 text-center text-sm text-gray-400">{emptyText}</p>
                ) : (
                    <>
                        <div className="flex flex-col gap-2 pb-3">
                            {visible.map((theme) => (
                                <ThemeRow key={theme.id} theme={theme} />
                            ))}
                        </div>
                        {/* alwaysShow + mt-auto: footer pins to the bottom even on a
                            single page, so both columns keep a consistent height. */}
                        <Pagination
                            currentPage={safePage + 1}
                            totalPages={totalPages}
                            total={themes.length}
                            pageSize={PAGE_SIZE}
                            itemLabel="theme"
                            onPageChange={(p) => setPage(p - 1)}
                            className="mt-auto pt-3"
                            alwaysShow
                        />
                    </>
                )}
            </div>
        </div>
    );
}

interface SentimentThemeBoardProps {
    themes: SentimentTheme[];
}

// Splits themes into "needs attention" (negative net) and "what's working"
// (positive net), each ranked by call volume so the highest-impact items
// surface first. Replaces the old single endless list with a two-column,
// at-a-glance board. Neutral themes (net === 0) go to the working side so they
// don't read as problems.
export default function SentimentThemeBoard({
    themes,
}: SentimentThemeBoardProps) {
    const { negative, positive } = useMemo(() => {
        const byVolume = (a: SentimentTheme, b: SentimentTheme) =>
            b.call_count - a.call_count;
        const negative = themes
            .filter((t) => t.avg_sentiment < 0)
            .sort(byVolume);
        const positive = themes
            .filter((t) => t.avg_sentiment >= 0)
            .sort(byVolume);
        return { negative, positive };
    }, [themes]);

    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ThemeColumn
                title="Needs attention"
                icon={<ThumbsDownIcon className="h-4 w-4" />}
                accent="text-rose-500"
                themes={negative}
                emptyText="No negative themes in this range."
            />
            <ThemeColumn
                title="What's working"
                icon={<ThumbsUpIcon className="h-4 w-4" />}
                accent="text-emerald-500"
                themes={positive}
                emptyText="No positive themes in this range."
            />
        </div>
    );
}
